import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { AuthProvider, useAuth } from "./context/AuthContext";

import { FilterProvider } from "./context/FilterContext";
import { ThemeProvider } from './context/ThemeContext';

const LoginPage = lazy(() => import("./pages/Auth/LoginPage"));
const AuthCallback = lazy(() => import("./pages/Auth/AuthCallback"));
const DashboardPage = lazy(() => import("./pages/Dashboard/DashboardPage"));
const AnalyticsPage = lazy(() => import("./pages/Analytics/AnalyticsPage"));
const UploadsPage = lazy(() => import("./pages/Attribution/UploadPage"));
const SettingsPage = lazy(() => import("./pages/Settings/SettingsPage"));
const MetaAudiencesPage = lazy(() => import("./pages/MetaAudiences/MetaAudiencesPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // apiClient handles 401 retries already
      retry: (failureCount, error) => {
        if (error?.message?.includes("Unauthorized")) {
          return false
        }
        return failureCount < 2
      }
    }
  }
})

function AppRoutes() {
  const { token } = useAuth();

  return (
    <Suspense fallback={<div className="loader-wrapper" role="status" aria-label="Loading page"><div className="loader" /></div>}>
      <Routes>

      {/* Landing page */}
      <Route
        path="/"
        element={token ? <Navigate to="/dashboard" /> : <LoginPage />}
      />
      <Route
        path='/login'
        element={token ? <Navigate to='/dashboard' /> : <LoginPage />}
      />

      {/* Microsoft redirect */}
      <Route path="/logged-in" element={<AuthCallback />} />

      {/* Protected dashboard */}
      <Route
        path="/dashboard"
        element={token ? <DashboardPage /> : <Navigate to="/" />}
      />
      <Route
        path="/analytics"
        element={token ? <AnalyticsPage /> : <Navigate to="/" />}
      />
      <Route
        path="/sales"
        element={token ? <UploadsPage /> : <Navigate to="/" />}
      />
      <Route
        path="/settings"
        element={token ? <SettingsPage /> : <Navigate to="/" />}
      />
      <Route
        path="/meta-audiences"
        element={token ? <MetaAudiencesPage /> : <Navigate to="/" />}
      />

      </Routes>
    </Suspense>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: '' };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ componentStack: info.componentStack });
  }

  render() {
    if (this.state.error) {
      return (
        <main role='alert' style={{ maxWidth: 720, margin: '8vh auto', padding: 24, color: '#00338d', background: '#f8fafc', borderRadius: 8 }}>
          <h1>Dashboard could not load</h1>
          <p>{this.state.error.message}</p>
          <pre style={{ overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{this.state.componentStack}</pre>
        </main>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <FilterProvider>
              <Router>
                <AppRoutes />
              </Router>
            </FilterProvider>
          </ThemeProvider>
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
